/** @prettier */
import React, { useState, useReducer, useEffect } from "react";
import { Button, Panel, Checkbox, PanelGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./CategoriesAccordion.css";
import {
    SubcategoryTreeData,
    ProductTreeData,
    TreeData,
    getProductTree,
} from "./createTestApi";

/* =====
 * State
 * ===== */

type CategorySelectionAction =
    | { type: "toggle"; uuid: number }
    | { type: "select"; uuids: number[] }
    | { type: "deselect"; uuids: number[] };

const categorySelectionReducer = (
    selectedCategories: Set<number>,
    action: CategorySelectionAction,
) => {
    if (action.type === "toggle") {
        if (selectedCategories.has(action.uuid)) {
            selectedCategories.delete(action.uuid);
        } else {
            selectedCategories.add(action.uuid);
        }
    } else {
        selectedCategories = action.uuids.reduce((state, id) => {
            action.type === "select" ? state.add(id) : state.delete(id);
            return state;
        }, selectedCategories);
    }
    return new Set(selectedCategories);
};
export const useCategoriesReducer = () =>
    useReducer(categorySelectionReducer, new Set<number>());

export type CategorySelectionState = ReturnType<typeof useCategoriesReducer>;

const categoryGroupUuids = (
    categoryGroup: Exclude<TreeData, SubcategoryTreeData>,
): number[] => {
    return (categoryGroup.children as Array<
        Exclude<TreeData, ProductTreeData>
    >).flatMap(category => {
        return category.type === "SubCategory"
            ? [category.subCategoryId]
            : categoryGroupUuids(category);
    });
};

const categoryGroupSelectionState = (
    selectedCategories: Set<number>,
    { children }: Exclude<TreeData, SubcategoryTreeData>,
): "all" | "some" | "none" => {
    let foundSelected = false;
    let foundDeselected = false;
    for (const category of children) {
        const childState =
            category.type === "SubCategory"
                ? selectedCategories.has(category.subCategoryId)
                    ? "all"
                    : "none"
                : categoryGroupSelectionState(selectedCategories, category);
        if (childState === "some") return "some";
        if (childState === "all") {
            foundSelected = true;
        }
        if (childState === "none") {
            foundDeselected = true;
        }
        if (foundSelected && foundDeselected) return "some";
    }
    if (foundSelected) return "all";
    return "none";
};

/* ====
 * View
 * ==== */

export type QuestionSelectorAccordionProps = {
    productId: string;
    state: CategorySelectionState;
};
const QuestionSelectorAccordion = ({
    productId,
    state,
}: QuestionSelectorAccordionProps) => {
    const [productTree, setProductTree] = useState<
        ProductTreeData | undefined
    >();

    useEffect(() => {
        getProductTree(productId).then(setProductTree);
    }, [productId]);

    return productTree ? (
        <div id="question-categories">
            {categoryOrGroup(productTree, state)}
        </div>
    ) : (
        <FontAwesomeIcon icon="spinner" size="2x" spin />
    );
};
export default QuestionSelectorAccordion;

const categoryOrGroup = (category: TreeData, state: CategorySelectionState) =>
    category.type === "SubCategory" ? (
        <Category key={category.name} category={category} state={state} />
    ) : (
        <CategoryGroup key={category.name} category={category} state={state} />
    );
const Category = ({
    category: { name, count, subCategoryId },
    state: [state, dispatch],
}: {
    category: SubcategoryTreeData;
    state: CategorySelectionState;
}) => (
    <Panel bsStyle="">
        <Panel.Heading>
            <Checkbox
                inline
                checked={state.has(subCategoryId)}
                onChange={() =>
                    dispatch({ type: "toggle", uuid: subCategoryId })
                }
            >
                {name} <span className="category-count">({count})</span>
            </Checkbox>
        </Panel.Heading>
    </Panel>
);

const CategoryGroup = ({
    category,
    state,
}: {
    category: Exclude<TreeData, SubcategoryTreeData>;
    state: CategorySelectionState;
}) => {
    const { name, children } = category;
    const [selectedCategories, dispatch] = state;
    const [isOpen, setOpen] = useState(category.type === "Product");
    const toggleOpen = () => setOpen(t => !t);
    const selectionState = categoryGroupSelectionState(
        selectedCategories,
        category,
    );
    return (
        /* TODO: fix warning from empty bs style */
        <Panel expanded={isOpen} onToggle={() => {}} bsStyle="">
            <Panel.Heading>
                <Checkbox
                    inputRef={el =>
                        el && (el.indeterminate = selectionState === "some")
                    }
                    inline
                    checked={selectionState === "all"}
                    onChange={() => {
                        dispatch({
                            type:
                                selectionState === "all"
                                    ? "deselect"
                                    : "select",
                            uuids: categoryGroupUuids(category),
                        });
                    }}
                >
                    {name}{" "}
                    <span className="category-count">({category.count})</span>
                </Checkbox>
                <Button bsStyle="link" onClick={toggleOpen}>
                    <FontAwesomeIcon
                        size="lg"
                        icon={isOpen ? "angle-down" : "angle-right"}
                    />
                </Button>
            </Panel.Heading>
            <Panel.Collapse>
                <Panel.Body>
                    <PanelGroup id="categories">
                        {(children as Array<
                            Exclude<TreeData, ProductTreeData>
                        >).map(category => categoryOrGroup(category, state))}
                    </PanelGroup>
                </Panel.Body>
            </Panel.Collapse>
        </Panel>
    );
};
